import React, { useRef, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { withStyles } from 'tss-react/mui';
import { Box, Popper, ClickAwayListener, Grow, Paper } from '@mui/material';
import SaveVideoSettingsButton from './SaveVideoSettingsButton';
import SavingPanel from './saving/SavingPanel';
import { createActions } from '../../store';

// Styles for the dropdown container and popper
const styles = (theme) => ({
    dropdownContainer: {
        position: 'relative',
        display: 'inline-block',
    },
    popper: {
        zIndex: 1301,
        marginTop: '10px !important',
    },
    paper: {
        minWidth: "360px",
        marginTop: theme.spacing(1),
        boxShadow: theme.shadows[8],
    },
});

const SaveVideoDropdown = ({ classes, save, savingPanelOpen, setProperty, videoSavingState, savingInProgress }) => {
    const anchorRef = useRef(null);
    const closeTimerIdRef = useRef(null);
    
    // Close dropdown when clicking outside
    const onClickAway = useCallback((event) => {
        if ((anchorRef.current && anchorRef.current.contains(event.target)) || savingInProgress)
            return;

        setProperty('videoSavingState.savingPanelOpen', false);
    }, [setProperty, savingInProgress]);

    // Auto-close dropdown after successful completion
    useEffect(() => {
        // Don't auto-close if panel is closed, still saving, or no state available
        if (!savingPanelOpen || savingInProgress || !videoSavingState)
            return;

        // Check if all steps are completed
        const steps = Object.values(videoSavingState).filter(step => typeof step === 'object');
        const allCompleted = steps.length > 0 && steps.every(step => step.status === "completed" || step.status === "unchanged");
        
        if (allCompleted) {
            if (!closeTimerIdRef.current) {
                // Set new timeout and store the ID in the ref.
                // Auto-close after 1.2 seconds
                closeTimerIdRef.current = setTimeout(() => {
                    setProperty('videoSavingState.savingPanelOpen', false);
                    clearTimeout(closeTimerIdRef.current);
                    closeTimerIdRef.current = null;
                }, 1200);
            }
        }
    }, [videoSavingState, savingInProgress, savingPanelOpen, setProperty]);

    return (
        <Box className={classes.dropdownContainer}>
            <Box ref={anchorRef}>
                <SaveVideoSettingsButton
                    save={save}
                />
            </Box>
            <Popper
                open={savingPanelOpen && Boolean(anchorRef.current)}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                transition
                disablePortal={false}
                className={classes.popper}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === 'bottom-end' ? 'right top' : 'left top',
                        }}
                        timeout={500}
                    >
                        <Paper className={classes.paper}>
                            <ClickAwayListener onClickAway={onClickAway}>
                                <Box>
                                    <SavingPanel />
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Box>
    );
};

const mapStateToProps = (state) => ({
    savingPanelOpen: state.create.videoSavingState.savingPanelOpen,
    savingInProgress: state.create.videoSavingState.savingInProgress,
    videoSavingState: state.create.videoSavingState
});

const mapDispatchToProps = (dispatch) => ({
    setProperty: (key, value) => dispatch(createActions.setProperty(key, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(SaveVideoDropdown, styles));